import * as React from 'react';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { CustomField } from '@app/model/CustomField';
import { ChangeLogButton } from '@app/components/ChangeLogButton';
import { EntityChangeLog } from '@app/components/EntityChangeLog';
import { RemoveButton } from '@app/components/RemoveButton';
import {
  ActionList,
  ActionListItem,
  Alert,
  AlertVariant,
  Bullseye,
  Button,
  Card,
  EmptyState,
  EmptyStateBody,
  Form,
  FormAlert,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  PageSection,
  Pagination,
  PaginationVariant,
  SearchInput,
  Split,
  SplitItem,
  TextArea,
  TextInput,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Tooltip
} from '@patternfly/react-core';
import { PencilAltIcon, SearchIcon } from '@patternfly/react-icons';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { InnerScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { useEntity } from '@app/hooks/useEntity';
import { useCustomFieldStore } from '@app/store/entityStore';
import { useBreadcrumbLabel } from '@app/hooks/useBreadcrumbLabel';

const emptyCustomField: Partial<CustomField> = {
    name: '',
    type:  '',
    description: '',
    values: ''
}

const CustomFields: React.FunctionComponent = () => {
  type ValidateStatus = 'success' | 'warning' | 'error' | 'default';

  const { addNotification } = useNotification();
  useBreadcrumbLabel('Custom Fields');

  // Basic UI State
  const [page, setPage] = React.useState<number>(1);
  /**
   * The row whose change log is open, or null for none.
   *
   * A normalised {id, label} rather than the row itself: these three pages have no detail page, so
   * the panel is driven from here, and each of them names its rows with a different field.
   */
  const [changeLogRow, setChangeLogRow] = React.useState<{ id: number; label: string } | null>(null);
  const { perPage, setPerPage } = useTablePreferences('custom-fields', 10);
  const [filter, setFilter] = React.useState<string>('');
  const [isEditing, setIsEditing] = React.useState(false);

  // Data State
  const {items: rows, retry} = useEntity(useCustomFieldStore);
  const save = useCustomFieldStore(state => state.save);
  const remove = useCustomFieldStore(state => state.delete);

  const [customField, setCustomField] = React.useState<Partial<CustomField>>(emptyCustomField);
  const [validated, setValidated] = React.useState<Record<string, ValidateStatus>>({ 
    name: 'default', description: 'default', type: 'default', values: 'default' 
  });

  // --- Derived State (Logic Squeezed) ---
  const filteredRows = React.useMemo(() => {
    if (!filter) return rows;
    const search = filter.toLowerCase();
    return rows.filter(cf => 
      cf.name?.toLowerCase().includes(search) || 
      cf.description?.toLowerCase().includes(search)
    );
  }, [rows, filter]);

  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return filteredRows.slice(startIdx, startIdx + perPage);
  }, [filteredRows, page, perPage]);

  const valuesRequired = React.useMemo(() => {
    return customField.type === 'select' || customField.type === 'checkbox';
  }, [customField.type]);

  // --- Handlers ---
  const resetForm = () => {
    setCustomField(emptyCustomField);
    setValidated({ name: 'default', description: 'default', type: 'default', values: 'default' });
    setIsEditing(false);
  };

  const validate = () => {
    const newValidated: Record<string, ValidateStatus> = {
      name: customField.name ? 'success' : 'error',
      description: customField.description ? 'success' : 'error',
      type: customField.type ? 'success' : 'error',
      values: (valuesRequired && !customField.values) ? 'error' : 'success'
    };
    setValidated(newValidated);
    return !Object.values(newValidated).includes('error');
  };

  const onSaveClick = async () => {
    if (!validate()) return;

    beginLoading();
    try {
      const isNew = customField.id ? false : true;
      await save(customField, isNew);

      if (isNew) {
        addNotification('Custom field updated with success', AlertVariant.success);
      } else {
        addNotification('Custom field added with success.', AlertVariant.success);
      }
      resetForm();
    } catch (error: any) {
      addNotification(error.error || 'Operation failed', AlertVariant.danger);
    } finally {
      endLoading();
    }
  };

  const onRemoveConfirm = async (cf: CustomField) => {
    beginLoading();
    try {
      await remove(cf.id);
      addNotification('Custom field removed with success', AlertVariant.success);
      if (customField.id === cf.id) resetForm();
    } catch (error: any) {
      addNotification(error.error || 'Removal failed', AlertVariant.danger);
    } finally {
      endLoading();
    }
  };

  const typeOptions = [
    { value: '', label: 'Pick one...', disabled: true, isPlaceholder: true },
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'select', label: 'Select' },
    { value: 'checkbox', label: 'Check Box' }
  ];

  return (
    <EntityChangeLog
      entity="customfield"
      id={changeLogRow?.id}
      entityLabel="this custom field"
      title={changeLogRow?.label}
      isOpen={!!changeLogRow}
      onClose={() => setChangeLogRow(null)}
    >
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">Instructor Custom Fields</Title>
          </SplitItem>
          <SplitItem isFilled></SplitItem>
          <SplitItem>
            <ActionList>
              {isEditing ? (
                <>
                  <ActionListItem>
                    <Button variant="secondary" onClick={resetForm}>Cancel</Button>
                  </ActionListItem>
                  <ActionListItem>
                    <Button variant="primary" onClick={onSaveClick}>Save</Button>
                  </ActionListItem>
                </>
              ) : (
                <ActionListItem>
                  <Button variant="primary" onClick={() => setIsEditing(true)}>New Custom Field</Button>
                </ActionListItem>
              )}
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      <PageSection hasBodyWrapper isWidthLimited>
        <Card isPlain className="form-same-list-page">
          <Form isHorizontal id="form-custom-fields" style={{ padding: '24px' }}>
            {Object.values(validated).includes('error') && (
              <FormAlert>
                <Alert variant="danger" title="Fill out all required fields before continuing." aria-live="polite" isInline />
              </FormAlert>
            )}
            <FormGroup label="Name" isRequired fieldId="name">
              <TextInput 
                isRequired type="text" id="name" value={customField.name} 
                isDisabled={!isEditing} validated={validated.name}
                onChange={(_e, val) => setCustomField({ ...customField, name: val })} 
              />
              <FormHelperText><HelperText><HelperTextItem>Field name displayed on screen.</HelperTextItem></HelperText></FormHelperText>
            </FormGroup>

            <FormGroup label="Description" isRequired fieldId="description">
              <TextInput 
                isRequired type="text" id="description" value={customField.description} 
                isDisabled={!isEditing} validated={validated.description}
                onChange={(_e, val) => setCustomField({ ...customField, description: val })} 
              />
              <FormHelperText><HelperText><HelperTextItem>Help text displayed within the field.</HelperTextItem></HelperText></FormHelperText>
            </FormGroup>

            <FormGroup label="Field Type" isRequired fieldId="type">
              <FormSelect id='fieldType'
                value={customField.type} isDisabled={!isEditing} validated={validated.type}
                onChange={(_e, val) => setCustomField({ ...customField, type: val, values: (val === 'select' || val === 'checkbox') ? customField.values : '' })}
              >
                {typeOptions.map((opt, i) => (
                  <FormSelectOption key={i} value={opt.value} label={opt.label} isDisabled={opt.disabled} isPlaceholder={opt.isPlaceholder} />
                ))}
              </FormSelect>
            </FormGroup>

            {valuesRequired && (
              <FormGroup label="Field Values" fieldId="values" isRequired>
                <TextArea 
                  value={customField.values} isDisabled={!isEditing} validated={validated.values}
                  onChange={(_e, val) => setCustomField({ ...customField, values: val })} 
                />
                <FormHelperText><HelperText><HelperTextItem>Add one value per line.</HelperTextItem></HelperText></FormHelperText>
              </FormGroup>
            )}
          </Form>
        </Card>
      </PageSection>

      <PageSection hasBodyWrapper isWidthLimited>
        <Card>
          <div style={{ padding: '24px' }}>
            <div style={{ 
              opacity: isEditing ? 0.4 : 1, 
              pointerEvents: isEditing ? 'none' : 'auto', 
              transition: 'opacity 0.3s ease' 
            }}>
              <Toolbar id="custom-fields-toolbar">
                <ToolbarContent>
                  <ToolbarGroup>
                    <ToolbarItem>
                      <SearchInput
                        placeholder="Filter by name..." value={filter}
                        onChange={(_e, val) => { setFilter(val); setPage(1); }}
                        onClear={() => { setFilter(''); setPage(1); }}
                      />
                    </ToolbarItem>
                  </ToolbarGroup>
                  <ToolbarItem variant="pagination">
                    <Pagination
                      isCompact itemCount={filteredRows.length} page={page} perPage={perPage}
                      onSetPage={(_e, p) => setPage(p)}
                      onPerPageSelect={(_e, pp, p) => { setPerPage(pp); setPage(p); }}
                      variant={PaginationVariant.top}
                    />
                  </ToolbarItem>
                </ToolbarContent>
              </Toolbar>

              <div style={{ border: '1px solid #d2d2d2', borderRadius: '4px' }}>
                {/* Frozen Actions column: the scroll container is the horizontal scroll it sticks to, and
                    gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
                <InnerScrollContainer>
                  <Table aria-label="Custom Fields Table" variant="compact" gridBreakPoint="">
                    <Thead>
                      <Tr>
                        <Th>Name</Th>
                        <Th>Description</Th>
                        <Th>Type</Th>
                        <Th>Values</Th>
                        <Th width={10} isStickyColumn stickyMinWidth="160px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {paginatedRows.map((row) => (
                        <Tr key={row.id} isRowSelected={customField.id === row.id}>
                          <Td dataLabel="Name"><b>{row.name}</b></Td>
                          <Td dataLabel="Description">{row.description}</Td>
                          <Td dataLabel="Type">{row.type}</Td>
                          <Td dataLabel="Values" style={{ whiteSpace: 'pre-wrap' }}>{row.values}</Td>
                          <Td dataLabel="Actions"
                            isStickyColumn stickyMinWidth="160px" stickyRightOffset="0" hasLeftBorder>
                            <ActionList>
                              <ActionListItem>
                                <Tooltip content="Edit">
                                  <Button 
                                    variant="plain" icon={<PencilAltIcon />} isDisabled={isEditing}
                                    onClick={() => { setCustomField(row); setIsEditing(true); }} 
                                  />
                                </Tooltip>
                              </ActionListItem>
                              <ActionListItem>
                                <RemoveButton 
                                  isDisabled={isEditing} confirmationMessage={`Delete field "${row.name}"?`}
                                  onRemoveConfirm={() => onRemoveConfirm(row)} 
                                />
                              </ActionListItem>
                              <ActionListItem>
                                <ChangeLogButton recordLabel={row.name} onClick={() => setChangeLogRow({ id: row.id, label: row.name })} />
                              </ActionListItem>
                            </ActionList>
                          </Td>
                        </Tr>
                      ))}
                      {filteredRows.length === 0 && (
                        <Tr>
                          <Td colSpan={5}>
                            <Bullseye>
                              <EmptyState headingLevel="h5" icon={SearchIcon} titleText="No results found">
                                <EmptyStateBody>No custom fields match your filter.</EmptyStateBody>
                              </EmptyState>
                            </Bullseye>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </InnerScrollContainer>
              </div>
            </div>
          </div>
        </Card>
      </PageSection>
    </EntityChangeLog>
  );
};

export { CustomFields };